import "../sentryInstrument";
import React, { useState, useEffect } from 'react';
import Cookies from 'js-cookie';
import * as Sentry from "@sentry/browser";

import AddressForm from './AddressForm';
import { useCart } from './CartContext';
import { isValidEmail } from '../util/isValidEmail';


function CheckoutAddressFormContainer() {
  const { 
    setCartTax, 
    setShipping, 
    setCart,
    setCartTotal, 
    checkoutFormData, 
    setCheckoutFormData ,
    shippingSameAsBilling,
    shippingRequired,
    checkoutErrors,
    setShippingSameAsBilling,
    setFetchingShippingCost,
    setFetchingTaxCost
  } = useCart();
  const [shippingOptions, setShippingOptions] = useState([]);
  const [fetchingShippingRates, setFetchingShippingRates] = useState(false);
  const [shippingError, setShippingError] = useState('');
  const csrftoken = Cookies.get('csrftoken');
  const showShippingOptions = 
    (checkoutFormData.billing.addressComplete && shippingSameAsBilling && shippingError === '' && isValidEmail(checkoutFormData.billing.email)) || 
    (checkoutFormData.shipping.addressComplete && shippingError === '' && isValidEmail(checkoutFormData.billing.email));


  // fetch user data
  useEffect(() => {
    async function fetchUserData() {

      try {

        let data = {};
        let isBillingAddressComplete = false;
        let isShippingAddressComplete = false;
  
        // check session first and assign that data if already set
        const response1 = await fetch("/store/_api/customer/");
        if (!response1.ok) {
          throw new Error('Network response was not ok');
        }
        data = await response1.json();
        
        // if no data fallback to account info
        if (Object.entries(data).length === 0) {
          try {
            const response2 = await fetch("/accounts/_api/account/");
            
            if (!response2.ok) {
              if (response2.status === 400 || response2.status === 404) {
                throw new Error("Endpoint does not exist or bad request.");
              } else {
                throw new Error("server error occurred:", response2.status);
              }
            } else {
              data = await response2.json(); 
            }

          } catch (error) {
            Sentry.captureException(error);
          }
        }

        if (
          data.billing_address.address1 !== '' &&
          data.billing_address.city !== '' &&
          data.billing_address.region !== '' &&
          data.billing_address.postalCode !== '' &&
          data.billing_address.country !== ''
        ) {
          isBillingAddressComplete = true;
        }

        if (
          data.shipping_address.address1 !== '' &&
          data.shipping_address.city !== '' &&
          data.shipping_address.region !== '' &&
          data.shipping_address.postalCode !== '' &&
          data.shipping_address.country !== ''
        ) {
          isShippingAddressComplete = true;
        }

        setCheckoutFormData(prevState => ({
          ...prevState,
          billing: {
            ...prevState.billing,
            firstName: data.first_name || '',
            lastName: data.last_name || '',
            email: data.email || '',
            phone: data.phone || '',
            street1: data.billing_address.address1 || '',
            street2: data.billing_address.address2 || '',
            city: data.billing_address.city || '',
            region: data.billing_address.region || '',
            postalCode: data.billing_address.postal_code || '',
            country: data.billing_address.country,
            addressComplete: isBillingAddressComplete,
          },
          shipping: {
            ...prevState.shipping,
            firstName: data.first_name || '',
            lastName: data.last_name || '',
            street1: data.shipping_address.address1 || '',
            street2: data.shipping_address.address2 || '',
            city: data.shipping_address.city || '',
            region: data.shipping_address.region || '',
            postalCode: data.shipping_address.postal_code || '',
            country: data.shipping_address.country,
            addressComplete: isShippingAddressComplete,
          },
        }));

      } catch (error) {
        Sentry.captureException(error);  
      }

    }

    fetchUserData();
  }, []);


  // Fetch Shipping Rates:  
  // requires complete shipping address
  async function fetchShippingRates(controller) {
    setShippingOptions([]);
    setShipping({cost: 0, rateId: ""});
    setCartTax(0);
    setFetchingShippingRates(true);
    let shippingURL = '/store/_api/cart/shipping_rates/';

    if (shippingSameAsBilling && checkoutFormData.billing.addressComplete) {
      shippingURL += `?shipping_postal_code=${checkoutFormData.billing.postalCode}&shipping_region=${checkoutFormData.billing.region}&shipping_country=${checkoutFormData.billing.country}&shipping_street1=${checkoutFormData.billing.street1}`;
    } else if (checkoutFormData.shipping.addressComplete) {
      shippingURL += `?shipping_postal_code=${checkoutFormData.shipping.postalCode}&shipping_region=${checkoutFormData.shipping.region}&shipping_country=${checkoutFormData.shipping.country}&shipping_street1=${checkoutFormData.billing.street1}`;
    } 

    if (
      (checkoutFormData.billing.addressComplete && shippingSameAsBilling ) || 
      (checkoutFormData.billing.addressComplete && checkoutFormData.shipping.addressComplete)
    ) {
      
      try {
        const response = await fetch(shippingURL, {
          method: 'GET',
          headers: {"Content-Type": "application/json", "X-CSRFToken": csrftoken},
          signal: controller.signal,
        });
        
        if (!response.ok) {
          const errorObject = await response.json();
          const firstKey = Object.keys(errorObject)[0];
          setShippingError(errorObject[firstKey][0]);
        } else {
          setShippingError('');
          const data = await response.json();
          setShippingOptions(data);
        }

      } catch (error) {
        Sentry.captureMessage("there was a problem with fetch shipping rates");
        Sentry.captureException(error);
  
      }
    } else {
      setShippingOptions([]);
    }
    setFetchingShippingRates(false);
  }


  // Fetch And Set Tax Rates: 
  // Only used when shipping isn't required
  async function fetchAndSetTaxRate(controller) {
    setFetchingTaxCost(true);
    const formInfos = {
      email: checkoutFormData.billing.email,
      shipping_address1: checkoutFormData.billing.street1,
      shipping_address2: "",
      shipping_city: checkoutFormData.billing.city,
      shipping_country: checkoutFormData.billing.country,
      shipping_region: checkoutFormData.billing.region,
      shipping_postal_code: checkoutFormData.billing.postalCode,
      shipping_cost: 0,
    };      
  
    try {
      const response = await fetch("/store/_api/cart/set_tax/", {
        method: 'POST',
        body: JSON.stringify(formInfos),
        headers: {"Content-Type": "application/json", "X-CSRFToken": csrftoken},
        signal: controller.signal,
      });
      
      if (!response.ok) {
        throw new Error('Error getting shipping information.');
      }
      
      const data = await response.json();
      setCartTax(parseFloat(data.tax).toFixed(2))

    } catch (error) {
      Sentry.captureMessage("there was a problem with fetch tax data");
      Sentry.captureException(error);
    }
    setFetchingTaxCost(false);

  }


  // As form data is updated reveal next step
  // if shipping not required - calculate taxes
  // if shipping required - reveal shipping options
  useEffect(() => {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => {
      if (
        !shippingRequired && 
        isValidEmail(checkoutFormData.billing.email) && 
        checkoutFormData.billing.addressComplete
      ) {
        // fetch tax when shipping not required
        fetchAndSetTaxRate(controller);
      } else if (shippingRequired) {
        // fetch shipping rates when shipping required
        fetchShippingRates(controller);
      }
    }, 1000);

    return () => {
      clearTimeout(timeoutId);
      controller.abort();
    }
  }, [checkoutFormData, shippingSameAsBilling]);


  // hide/show shipping address
  function handleCheckboxChange() {
    setShippingSameAsBilling(!shippingSameAsBilling);
    setCartTax(0);
    setShipping({
      cost: 0, 
      id: "",
    });
  }


  async function fetchAndSetShippingAndTax(shippingInfos) {
    try {
      const response = await fetch('/store/_api/cart/shipping_rates/', {
        method: 'POST',
        body: shippingInfos,
        headers: {"X-CSRFToken": csrftoken},
      });
  
      if (!response.ok) {
        throw new Error('Error getting shipping cost and tax information.');
      }
  
      const data = await response.json();
      setCart(data);

      return {
        shippingCost: parseFloat(data.shipping_cost).toFixed(2),
        tax: parseFloat(data.tax).toFixed(2),
        total: parseFloat(data.total).toFixed(2),
      };
    } catch (error) {
      Sentry.captureMessage("there was a problem setting shipping and tax");
      Sentry.captureException(error);
      return { shippingCost: 0, tax: 0, total: 0 };
    }
  }


  async function handleShippingChange(event) {
    setFetchingShippingCost(true);
    setFetchingTaxCost(true);
    const selectedOption = event.target.options[event.target.selectedIndex];
    const rateId = selectedOption.getAttribute('data-rate-id');
  
    const shippingInfos = new FormData();
    shippingInfos.append("shipping_rate_id", rateId);
    shippingInfos.append("email", checkoutFormData.billing.email);
  
    if (shippingSameAsBilling && checkoutFormData.billing.addressComplete) {
      shippingInfos.append("shipping_street1", checkoutFormData.billing.street1);
      shippingInfos.append("shipping_street2", checkoutFormData.billing.street2 || '');
      shippingInfos.append("shipping_city", checkoutFormData.billing.city);
      shippingInfos.append("shipping_postal_code", checkoutFormData.billing.postalCode);
      shippingInfos.append("shipping_region", checkoutFormData.billing.region);
      shippingInfos.append("shipping_country", checkoutFormData.billing.country);
    } else if (checkoutFormData.shipping.addressComplete) {
      shippingInfos.append("shipping_street1", checkoutFormData.shipping.street1);
      shippingInfos.append("shipping_street2", checkoutFormData.shipping.street2 || '');
      shippingInfos.append("shipping_city", checkoutFormData.shipping.city);
      shippingInfos.append("shipping_postal_code", checkoutFormData.shipping.postalCode);
      shippingInfos.append("shipping_region", checkoutFormData.shipping.region);
      shippingInfos.append("shipping_country", checkoutFormData.shipping.country);
    }
  
    const { shippingCost, tax, total } = await fetchAndSetShippingAndTax(shippingInfos);
    setShipping({
      rateId: rateId,
      cost: shippingCost,
    });
    setCartTax(tax);
    setCartTotal(total);
    setFetchingShippingCost(false);
    setFetchingTaxCost(false);
  }


  return (
    <div className="checkout-body">
      <fieldset>
        <h2>{window.gettext("Billing Information")}</h2>
        <AddressForm type="billing" />
      </fieldset>
      {shippingRequired &&
        <fieldset>
          <h2>{window.gettext("Shipping Information")}</h2>
          <div id="div_id_shipping_same_as_billing" className="form-group check  ">
            <input 
              type="checkbox" 
              name="shipping_same_as_billing" 
              id="id_shipping_same_as_billing" 
              checked={shippingSameAsBilling} 
              onChange={handleCheckboxChange}
              /> 
            <label className="mt-0" htmlFor="id_shipping_same_as_billing">{window.gettext("Shipping same as billing")}</label>
          </div>

          {!shippingSameAsBilling && <AddressForm type="shipping" />}

          <div className="form-group-row">
            
            {showShippingOptions ? 
              (<div 
                id="div_id_shipping_method" 
                className={checkoutErrors.shipping_method?.length >= 1 ? "form-group error" : "form-group"}
                >        
                <label htmlFor="id_shipping_method" className="select-label" >{window.gettext("Shipping Method")}</label>      
                <span className="select-wrap">	
                  <select 
                    name="shipping_method" 
                    className={fetchingShippingRates ? "form-control fetch-loading" : "form-control"}
                    id="id_shipping_method"
                    disabled={shippingOptions.length < 1 && !fetchingShippingRates}
                    onChange={handleShippingChange}
                    >  
                    <option value="default">{window.gettext("-- Shipping Options --")}</option>
                    {shippingOptions.map((option) => (
                      <option value={option.slug} key={option.slug} data-rate={option.rate} data-rate-id={option.id}>{option.label} - ${option.rate}</option>
                    ))}
                  </select>
                </span>
                {checkoutErrors.shipping_method?.length >= 1 && 
                  <ul>
                    {checkoutErrors.shipping_method.map((error, index) => (
                      <li key={index}>{error}</li>
                    ))}
                  </ul>
                }
              </div>)
            :
              (
                <div className="shipping-msg">
                  {shippingError !== '' && 
                    <p className="error">
                      {shippingError}
                    </p>
                  }
                  <p className="shipping-msg">Enter your full name, email, and address to view available shipping options</p>
                </div>
              )
            }
          </div>
        </fieldset>
      }
    </div>
  );
}

export default CheckoutAddressFormContainer;
